import {Action, Actions, Card, Separator} from 'platform/components';
import {
  Box,
  Center,
  getSize,
  HStack,
  Show,
  Text,
  useResponsivePropValue,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useRef} from 'react';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {hasSomeFilters} from '../../utils/hasSomeFilters';
import {FilterPresetButtons} from '../FilterPresetButtons/FilterPresetButtons';
import {useFilters} from '../FiltersContext/FiltersContext';
import {FilterTags} from '../FilterTags/FilterTags';
import {FilterFeatures} from './components/FilterFeatures/FilterFeatures';
import {FilterMakesModels} from './components/FilterMakesModels/FilterMakesModels';
import {FiltersNavigation} from './components/FilterNavigation/FiltersNavigation';
import {FilterSeller} from './components/FilterSeller/FilterSeller';
import {FilterSpecification} from './components/FilterSpecification/FilterSpecification';
import {FilterStatistics} from './components/FilterStatistics/FilterStatistics';
import {FilterBlockEnum} from './enums/FilterBlockEnum';

const PAGE_HEADER_HEIGHT_MOBILE = 24;
const PAGE_HEADER_HEIGHT_DESKTOP = 12;

export interface FiltersProps extends TestIdProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  count: number;
  onClose: () => void;
}

export function Filters(props: FiltersProps) {
  const tilesWrapperRef = useRef<HTMLDivElement>(null);
  const {filters} = useFilters();
  const formatNumber = useFormatNumber();
  const pageHeaderHeight = useResponsivePropValue([
    PAGE_HEADER_HEIGHT_MOBILE,
    PAGE_HEADER_HEIGHT_MOBILE,
    PAGE_HEADER_HEIGHT_DESKTOP,
  ]);

  const filterPresetActions: Action[] = [
    {
      type: 'button',
      title: i18n.t('page.filters.actions.showOffersWithCount', {
        count: props.count,
        formattedCount: formatNumber(props.count, 0),
      }),
      onClick: () => {
        props.onClose();
      },
      'data-testid': suffixTestId('showOffersWithCount', props),
    },
  ];

  return (
    <VStack height={`calc(100vh - ${getSize(pageHeaderHeight)})`}>
      <Box backgroundColor="palettes.white.10.100">
        <Box paddingHorizontal={4} paddingVertical={3}>
          <HStack spacing={4}>
            <Center height={8}>
              <Text size="large" alternative>
                {i18n.t('page.filters.labels.filters')}
              </Text>
            </Center>
            <Show when={!props.filterPresetsControl.isEmpty}>
              <Separator orientation="vertical" spacing={0} />
            </Show>
            <FilterPresetButtons
              filterPresetsControl={props.filterPresetsControl}
              isSortingEnabled
            />
            <Center height={8}>
              <Actions
                actions={filterPresetActions}
                data-testid={suffixTestId('filterLayoutActions', props)}
              />
            </Center>
          </HStack>
        </Box>
        <Separator spacing={0} />
        <Show when={hasSomeFilters(filters) || props.filterPresetsControl.isOutdated}>
          <Box padding={4}>
            <FilterTags
              filterPresetsControl={props.filterPresetsControl}
              showSaveButton
              data-testid={suffixTestId('filterTags', props)}
            />
          </Box>
          <Separator spacing={0} />
        </Show>
      </Box>
      <Box flex={1} overflow="hidden">
        <HStack height="100%">
          <Box padding={4} paddingRight={0} minWidth={48}>
            <FiltersNavigation
              tilesWrapperRef={tilesWrapperRef}
              data-testid={suffixTestId('filtersNavigation', props)}
            />
          </Box>
          <Box id="tilesWrapper" flex={1} padding={4} overflowX="hidden" ref={tilesWrapperRef}>
            <VStack spacing={4}>
              <Box id={FilterBlockEnum.seller}>
                <Card title={i18n.t('page.filters.labels.seller')}>
                  <FilterSeller data-testid={suffixTestId('filterSeller', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.statistics}>
                <Card title={i18n.t('page.filters.labels.statistics')}>
                  <FilterStatistics data-testid={suffixTestId('filterStatistics', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.makesAndModels}>
                <Card title={i18n.t('page.filters.labels.makeAndModels')}>
                  <FilterMakesModels data-testid={suffixTestId('filterMakesModels', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.specification}>
                <Card title={i18n.t('page.filters.labels.specification')}>
                  <FilterSpecification data-testid={suffixTestId('filterSpecification', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.features}>
                <Card title={i18n.t('general.labels.allFeatures')}>
                  <FilterFeatures data-testid={suffixTestId('filterFeatures', props)} />
                </Card>
              </Box>
            </VStack>
          </Box>
        </HStack>
      </Box>
    </VStack>
  );
}
